import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { mediaApi } from '../../services/api';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Reusable Image Uploader component.
 *
 * Props:
 *   value          - current image URL (string)
 *   onChange       - callback(newUrl)
 *   collection     - folder path (e.g., "sections/hero", "sections/team")
 *   recommendedSize- display hint (e.g., "600×400px")
 *   label          - optional label text
 *   aspectRatio    - optional for preview display ("1/1", "3/2", etc.)
 */
export default function ImageUploader({
  value = '',
  onChange,
  collection = 'sections',
  recommendedSize,
  label = 'Gambar',
  aspectRatio,
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    if (!file.type.startsWith('image/')) {
      setError('Hanya file gambar yang diizinkan (JPG, PNG, WebP, GIF)');
      return;
    }

    // Validate size
    if (file.size > MAX_FILE_SIZE) {
      setError('Ukuran file maksimal 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const fd = new FormData();
      fd.append('file', file);
      if (collection) fd.append('collection', collection);

      const res = await mediaApi.upload(fd);
      const uploadedUrl = res.data.data?.url || '';
      onChange(uploadedUrl);
    } catch (err) {
      setError('Gagal upload gambar: ' + (err.response?.data?.message || 'Unknown error'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = () => {
    onChange('');
    setError('');
  };

  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}

      {/* Preview */}
      {value ? (
        <div className="relative group mb-2">
          <img
            src={value}
            alt="Preview"
            className="w-full h-40 object-cover rounded-xl border border-gray-200"
            style={aspectRatio ? { aspectRatio } : undefined}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded-xl flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <label className="bg-white text-gray-700 cursor-pointer px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors">
              📸 Ganti
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" disabled={uploading} />
            </label>
            <button type="button" onClick={handleRemove} className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-600 transition-colors">🗑️ Hapus</button>
          </div>
        </div>
      ) : (
        <label className={`flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
          error ? 'border-red-300 bg-red-50' : uploading ? 'border-gray-300 bg-gray-50 cursor-wait' : 'border-gray-300 hover:border-primary/50 hover:bg-primary/5'
        }`}>
          {uploading ? (
            <>
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-gray-500">Mengupload...</span>
            </>
          ) : (
            <>
              <ImageIcon className={`w-10 h-10 ${error ? 'text-red-400' : 'text-gray-400'}`} />
              <span className={`text-sm font-medium ${error ? 'text-red-600' : 'text-gray-600'}`}>
                Klik untuk upload gambar
              </span>
              <span className="text-xs text-gray-400">JPG, PNG, WebP, GIF (max 5MB)</span>
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" disabled={uploading} />
        </label>
      )}

      {/* Recommended size hint */}
      {recommendedSize && (
        <div className="flex items-center gap-1 mt-1.5">
          <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-xs text-amber-600">Rekomendasi ukuran: <strong>{recommendedSize}</strong></span>
        </div>
      )}

      {/* Folder destination hint */}
      {collection && (
        <p className="text-xs text-gray-400 mt-1">
          📁 Disimpan di: <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px]">/{collection}/</code>
        </p>
      )}

      {/* Error */}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}